import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import type { Elemental } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import { unsaveElementalInAList } from '~~/src/api/elementals'
import type { List } from '~~/src/types/list'

function updateElementalLists(listId: string) {
  return (old?: Elemental | null) => {
    if (!old)
      return old
    if (!old.listIds)
      return
    return { ...old, listIds: old.listIds.filter(id => id !== listId) } as Elemental
  }
}
function revertElementalLists(listId: string) {
  return (old?: Elemental | null) => {
    if (!old || !old.listIds)
      return old
    return { ...old, listIds: [...old.listIds, listId] } as Elemental
  }
}
export const useRemoveSaveElementalInAList = () => {
  const queryClient = useQueryClient()
  return useMutation<boolean | null, Error, { elementalType: ElementalTypeEnum; id: string; listId: string; slug?: string }>({
    mutationKey: ['unsave-elemental-in-a-list'],
    mutationFn: async ({ id, listId }) => {
      return await unsaveElementalInAList(listId, id)
    },
    onMutate: async ({ elementalType, id, listId, slug }) => {
      queryClient.setQueriesData<Elemental[]>({
        queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
        exact: false,
      }, (old) => {
        if (!old || !old.length)
          return old
        return old.filter(e => e.id !== id)
      })
      queryClient.setQueriesData<List[]>({
        queryKey: [TanStackQueryIds.PROFILE_LIST_ELEMENTALS, elementalType],
        exact: false,
      }, (old) => {
        if (!old || !old.length)
          return old

        if (elementalType === ElementalTypeEnum.List)
          return old.filter(e => e.id !== id)

        const allList = old.shift()
        if (!allList)
          return old

        const newAll = { ...allList, prompts: allList.prompts?.filter(e => e.id !== id) }
        return [...old, newAll]
      })
      queryClient.setQueriesData<Elemental | null>({
        queryKey: ['elemental', elementalType, id],
        exact: false,
      }, updateElementalLists(listId))
      if (slug) {
        queryClient.setQueriesData<Elemental | null>({
          queryKey: ['elemental', elementalType, slug],
          exact: false,
        }, updateElementalLists(listId))
      }
    },
    onError: (_, { elementalType, id, listId, slug }) => {
      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', elementalType],
        exact: false,
        type: 'all',
      })
      queryClient.setQueriesData<Elemental | null>({
        queryKey: ['elemental', elementalType, id],
        exact: false,
      }, revertElementalLists(listId))
      if (slug) {
        queryClient.setQueriesData<Elemental | null>({
          queryKey: ['elemental', elementalType, slug],
          exact: false,
        }, revertElementalLists(listId))
      }
      queryClient.refetchQueries({
        queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
        exact: false,
      })
    },
    onSuccess: (_, { elementalType, listId }) => {
      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', elementalType],
        exact: false,
        type: 'all',
      })
      if (listId) {
        queryClient.refetchQueries({
          queryKey: [TanStackQueryIds.LIST_ELEMENTALS, listId],
          exact: false,
        })
      }
    },
  })
}
